import { useMemo } from 'react';

import { Route, Switch, useLocation } from 'react-router-dom';

import { FormGrid } from 'components';
import { Navbar } from 'components/Navbar';
import { PrivateRoute } from 'components/PrivateRoute';
import { TimeoutController } from 'components/TimeoutModal/TimeoutController';
import { ReviewedCandidateProvider } from 'contexts';
import { getReturnButtonPath } from 'helpers';
import { useUserContext } from 'hooks/useUserContext';
import { AuditorCandidateTable } from 'pages/dashboard/AuditorCandidateTable';
import { AuditorOrganizationTable } from 'pages/dashboard/AuditorOrganizationTable';
import { AuditorTrancheTable } from 'pages/dashboard/AuditorTrancheTable';
import { AuditorUserTable } from 'pages/dashboard/AuditorUserTable';
import { NotFound } from 'pages/error/NotFound';
import { PermissionDenied } from 'pages/error/PermissionDenied';
import { OrganizationReviewPage } from 'pages/organization_review/OrganizationReviewPage';
import { ReviewedCandidatePage } from 'pages/reviewed_candidate/ReviewedCandidatePage';
import { UserProps } from 'types';

const createAuthChecks = (user: UserProps | null) => [
  {
    check: () => user?.role === 'auditor',
    path: '/home/auditor_dashboard/403',
  },
];

export const ContentWrapper = () => {
  const location = useLocation<{ from?: string }>();
  const { user, loaded } = useUserContext();
  const authChecks = useMemo(() => createAuthChecks(user), [user]);

  // This stops the auth checks in each route from firing before the user data is loaded
  if (!user && !loaded) {
    // Still loading
    return null;
  }
  return (
    <ReviewedCandidateProvider>
      <Navbar
        profile={false}
        links={[
          { path: '/home/auditor_dashboard/tranches', label: 'Tranches' },
          {
            path: '/home/auditor_dashboard/reviewed_candidates',
            label: 'Candidates',
          },
          {
            path: '/home/auditor_dashboard/users',
            label: 'Users',
          },
          {
            path: '/home/auditor_dashboard/employers',
            label: 'Employers',
          },
        ]}
      />
      <Switch>
        {/* <PrivateRoute
          path='/home/signer_dashboard/tranches/:tranche_id'
          component={Tranche}
          authChecks={authChecks}
        /> */}
        <PrivateRoute
          path='/home/auditor_dashboard/reviewed_candidates/:candidate_id'
          component={() => (
            <FormGrid
              path={getReturnButtonPath(
                '/home/auditor_dashboard/reviewed_candidates',
                location.state?.from,
              )}
              label='RETURN TO CANDIDATES'
            >
              <ReviewedCandidatePage />
            </FormGrid>
          )}
          // exact
          authChecks={authChecks}
        />
        <PrivateRoute
          path='/home/auditor_dashboard/reviewed_candidates'
          component={AuditorCandidateTable}
          authChecks={authChecks}
        />
        <PrivateRoute
          path='/home/auditor_dashboard/tranches'
          component={AuditorTrancheTable}
          authChecks={authChecks}
        />
        <PrivateRoute
          path='/home/auditor_dashboard/employers/:org_id'
          component={() => (
            <FormGrid
              path={getReturnButtonPath(
                '/home/auditor_dashboard/employers',
                location.state?.from,
              )}
              label='RETURN TO EMPLOYERS'
            >
              <OrganizationReviewPage />
            </FormGrid>
          )}
          // exact
          authChecks={authChecks}
        />
        <PrivateRoute
          path='/home/auditor_dashboard/employers'
          component={AuditorOrganizationTable}
          authChecks={authChecks}
        />
        <PrivateRoute
          path='/home/auditor_dashboard/users'
          component={AuditorUserTable}
          authChecks={authChecks}
        />
        <Route
          path='/home/auditor_dashboard/403'
          component={PermissionDenied}
        />
        <Route component={NotFound} />
      </Switch>
      <TimeoutController />
    </ReviewedCandidateProvider>
  );
};
